import Router from 'vue-router';

import store from './store/index';
import { i18n } from '@/lang';

import MainLayout from './views/MainLayout';
import PageNotFound from './views/PageNotFound';

import ActivateAccountRoutes from './routes/activate-account';
import AddMemberRoutes from './routes/add-member';
import AdminRoutes from './routes/admin';
import AuthRoutes, { logout } from './routes/auth';
import BillingRoutes from './routes/billing';
import CalendarRoutes from './routes/calendar';
import ChallengesRoutes from './routes/challenges';
import CheckClub from './routes/switch-club';
import ClubMembersRoutes from './routes/club-members';
import CreateClubRoutes from './routes/create-club';
import CreateSessionRoutes from './routes/create-session';
import CrewsRoutes from './routes/crews';
import EventRoutes from './routes/event';
import EditClub from './routes/edit-club';
import EquipmentListRoutes from './routes/equipment-list';
import InvitationRoutes from './routes/invitation';
import HandlePaymentRoutes from './routes/handle-payment';
import HomeRoutes from './routes/home';
import LiveRoutes from './routes/live';
import NotificationsRoutes from './routes/notifications';
import ProfileRoutes from './routes/profile/profile';
import ProfileAthleteListRoutes from './routes/profile/profile-athlete-list';
import PublicLiveAdmin from './routes/public/admin';
import PublicLive from './routes/public/live';
import ReportsRoutes from './routes/reports/reports';
import SearchResultsRoutes from './routes/search-results';
import SessionRoutes from './routes/session';
import SettingsRoutes from './routes/settings';
import StyleguideRoutes from './routes/styleguide';
import SquadsRoutes from './routes/squads';
import UnauthorizedRoutes from './routes/unauthorized';
import UpgradeToPerformanceRoutes from './routes/upgrade-to-performance';
import UploadSessionFiles from './routes/upload-session-files';
import WelcomeRoutes from './routes/welcome';

import {
    ROUTES_BILLING,
    ROUTES_BILLING_LICENCE_EXPIRED,
    ROUTES_CREATE_CLUB,
    ROUTES_HOME,
    ROUTES_INVITATION_FOR_MEMBERSHIP_ACCEPT,
    ROUTES_INVITATION_FOR_MEMBERSHIP_REJECT,
    ROUTES_LOGIN,
    ROUTES_LOGOUT,
    ROUTES_SETTINGS_MY_CLUBS,
    ROUTES_SWITCH_CLUB,
    ROUTES_WELCOME,
    ROUTES_UNAUTHORIZED,
    ROUTES_UPGRADE_TO_PERFORMANCE
} from './constants/routes';
import { LUDUM_ERROR_CODES } from '@/constants';
const { NO_CLUBS, SUBSCRIPTION_EXPIRED } = LUDUM_ERROR_CODES;
import { LudumEventBus, CLOSE_SEARCH, CLOSE_MENU_DROPDOWN } from '@/event-bus';
import get from 'get-object-path';

Vue.use(Router);

const META_REQUIRES = [
    { field: 'requiresFocuses', action: 'clubHelper/getFocuses', requires: false },
    { field: 'requiresSpeedIntensities', action: 'clubHelper/getSpeedIntensities', requires: true },
    { field: 'requiresHeartRateIntensities', action: 'clubHelper/getHeartRateIntensities', requires: true },
    {
        field: 'requiresStrokeRateIntensitiesRowing',
        action: 'clubHelper/getStrokeRateIntensitiesRowing',
        requires: true
    },
    {
        field: 'requiresStrokeRateIntensitiesPaddling',
        action: 'clubHelper/getStrokeRateIntensitiesPaddling',
        requires: true
    },
    { field: 'requiresUnitesOfMeasurement', action: 'clubHelper/getUnitsOfMeasurement', requires: false }
];

async function resolveRoute(to, { field, action, requires }) {
    to.matched.some(({ meta }) => meta[field]) && (await store.dispatch(action, { requires }));
}

const routerConfig = {
    routes: [
        {
            path: '/dashboard',
            redirect: '/app/home'
        },
        {
            path: '/',
            redirect: '/home',
            component: MainLayout,
            meta: {
                requiresAuth: true
            },
            children: [
                AddMemberRoutes,
                BillingRoutes,
                CalendarRoutes,
                ChallengesRoutes,
                CheckClub,
                ClubMembersRoutes,
                CreateClubRoutes,
                CreateSessionRoutes,
                CrewsRoutes,
                EventRoutes,
                EditClub,
                EquipmentListRoutes,
                HomeRoutes,
                LiveRoutes,
                NotificationsRoutes,
                ProfileRoutes,
                ProfileAthleteListRoutes,
                ReportsRoutes,
                SearchResultsRoutes,
                SessionRoutes,
                SettingsRoutes,
                StyleguideRoutes,
                SquadsRoutes,
                UnauthorizedRoutes,
                UpgradeToPerformanceRoutes,
                UploadSessionFiles,
                WelcomeRoutes
            ]
        },
        AuthRoutes,
        AdminRoutes,
        ActivateAccountRoutes,
        HandlePaymentRoutes,
        InvitationRoutes,
        PublicLive,
        PublicLiveAdmin,
        logout,
        { path: '*', component: PageNotFound }
    ],
    mode: 'history'
};

const router = new Router(routerConfig);

router.beforeEach(function (to, from, next) {
    saveGoToRoute(to);
    store.dispatch('closeAllModals');
    LudumEventBus.$emit(CLOSE_SEARCH);
    LudumEventBus.$emit(CLOSE_MENU_DROPDOWN);

    if (to.matched.some(({ name }) => name === ROUTES_LOGOUT)) {
        store.dispatch('emptyClubs', []);
        store.commit('clubHelper/resetStore');
        Vue.auth.destroyToken();
        Vue.cookie.delete('selectedClub');
        store.commit('calendarQuickAssign/resetCalendarQuickAssignItems');
        store.commit('resetCalendarState');
        localStorage.clear();
        return next({ name: ROUTES_LOGIN });
    }

    const isAuthenticated = Vue.auth.isAuthenticated();
    if (isAuthenticated && to.matched.some(({ meta }) => meta.guest)) {
        return next('/');
    }
    if (to.matched.some(({ meta }) => meta.requiresAuth)) {
        if (isAuthenticated) {
            return store
                .dispatch('clubHelper/getMe')
                .then(async results => {
                    await store.dispatch('fetchUserClubs');

                    META_REQUIRES.forEach(meta => resolveRoute(to, meta));

                    // check user admin flag if route is admin
                    const { is_admin } = results.data;
                    if (to.matched.some(({ meta }) => meta.requiresAdmin) && !is_admin) {
                        return next({ name: ROUTES_HOME });
                    }

                    const name = await defineRedirectionState(to.name);
                    if (name) return next({ name });

                    return next();
                })
                .catch(error => {
                    store.dispatch('notifyError', { message: get(error, 'response.data.error') });
                    return next({ name: ROUTES_LOGOUT });
                });
        }

        return next({
            name: ROUTES_LOGIN
        });
    } else {
        return next();
    }
});

const excludeStates = [
    ROUTES_INVITATION_FOR_MEMBERSHIP_ACCEPT,
    ROUTES_INVITATION_FOR_MEMBERSHIP_REJECT,
    ROUTES_LOGIN,
    ROUTES_HOME,
    ROUTES_SWITCH_CLUB
];

function saveGoToRoute({ name, params, query }) {
    if (!excludeStates.includes(name)) {
        localStorage.setItem('ludumGoToState', JSON.stringify({ name, params, query }));
    }
}

// check if user has 420 (subscription expired) in his responses and redirect to appropriate pages
// NO_CLUBS (419) is now handled globally in the HTTP layer
async function defineRedirectionState(toState) {
    const { status } = await store.dispatch('checkToken');
    await store.dispatch('updateErrorCode', status);

    const route = store.getters.hasBillingPermissionInSelectedClub ? ROUTES_BILLING : ROUTES_BILLING_LICENCE_EXPIRED;
    if (status === SUBSCRIPTION_EXPIRED && ![ROUTES_SETTINGS_MY_CLUBS, route].includes(toState)) {
        return route;
    }
}

export function guardRoute(next, unauthorized = false, notInFeatureSet = false) {
    const message = notInFeatureSet
        ? i18n.t('thisFeatureDoesNotBelongToClubSubscriptionPlan')
        : i18n.t('notAuthorizedToSeeThisPage');
    const routeTo = notInFeatureSet ? ROUTES_UPGRADE_TO_PERFORMANCE : ROUTES_UNAUTHORIZED;
    const persist = notInFeatureSet;

    if (unauthorized || notInFeatureSet) {
        store.dispatch('notifyError', { message, persist });
        next({ name: routeTo });
    } else {
        next();
    }
}

export default router;
